<template>
  <Bar
    :options="computedChartOptions"
    :data="chartData"
    :dataset-id-key="datasetIdKey"
    :plugins="plugins"
    :update-mode="updateMode"
    :width="width"
    :height="height"
    data-test-name="bar-chart"
    data-test-variant=""
    data-test-state=""
  />
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { Bar } from 'vue-chartjs'
import * as ChartJS from 'chart.js'
import { cloneDeep, merge } from 'lodash-unified'
import { getChartDefaultOptions } from './utils'
import type { PropType } from 'vue'
import type { ChartData, ChartOptions, Plugin } from 'chart.js'

const {
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} = ChartJS
Chart.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

defineOptions({ name: 'BBarChart' })

const props = defineProps({
  datasetIdKey: {
    type: String,
    default: 'label',
  },
  width: {
    type: Number,
    default: 480,
  },
  height: {
    type: Number,
    default: 240,
  },
  plugins: {
    type: Array as PropType<Plugin<'bar'>[]>,
    default: () => [],
  },
  chartData: {
    type: Object as PropType<ChartData<'bar', number[], unknown>>,
    default: () => ({ datasets: [] }),
  },
  chartOptions: {
    type: Object as PropType<ChartOptions<'bar'>>,
    default: () => ({ responsive: true, maintainAspectRatio: true }),
  },
  updateMode: {
    type: String as PropType<
      | 'default'
      | 'reset'
      | 'resize'
      | 'none'
      | 'hide'
      | 'show'
      | 'active'
      | undefined
    >,
    default: undefined,
  },
})

const defaultOptions = cloneDeep(
  getChartDefaultOptions('bar')
) as ChartOptions<'bar'>

const isHorizontal = computed(() => props.chartOptions.indexAxis === 'y')
const isStacked = computed(
  () =>
    props.chartOptions.scales?.x?.stacked ??
    props.chartOptions.scales?.y?.stacked ??
    false
)
const computedChartOptions = computed(() =>
  merge(
    merge(defaultOptions, {
      elements: isStacked.value
        ? {
            bar: {
              borderSkipped: 'middle',
            },
          }
        : {},
      scales: {
        x: { grid: { display: isHorizontal.value } },
        y: { grid: { display: !isHorizontal.value } },
      },
    }),
    props.chartOptions
  )
)
</script>
